<template>
  <div class="report-tabs-wrap">
    <div class="report-tabs" ref="tabsEl">

      <template v-for="tab in tabs" :key="tab.to">

        <!-- Free tab — normal router-link -->
        <router-link
          v-if="!tab.premium || !freemium"
          :to="$url(tab.to)"
          class="tab-item"
          :class="{ 'tab-item--active': isActive(tab) }"
        >
          <font-awesome-icon :icon="tab.icon" class="tab-icon" />
          {{ tab.label }}
        </router-link>

        <!-- Premium tab — locked for freemium users -->
        <button
          v-else
          type="button"
          class="tab-item tab-item--locked"
          @click="freemiumPrompt"
        >
          <font-awesome-icon icon="lock" class="tab-icon tab-icon--lock" />
          {{ tab.label }}
        </button>

      </template>

    </div>

    <!-- Scroll fade indicators -->
    <div class="scroll-fade scroll-fade--left"  :class="{ visible: canScrollLeft  }" />
    <div class="scroll-fade scroll-fade--right" :class="{ visible: canScrollRight }" />
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import axios from 'axios';

export default {
  name: 'ReportHeaderTab',

  data() {
    return {
      freemium: true,
      toast: useToast(),
      canScrollLeft:  false,
      canScrollRight: false,

      tabs: [
        {
          label:   'Daily Sales',
          to:      '/all-reports',
          icon:    'chart-bar',
          premium: false,
          active:  [],
        },
        {
          label:   'Customer Report',
          to:      '/customer-report',
          icon:    'users',
          premium: true,
          active:  ['User Customer Report', 'Customer Invoice'],
        },
        {
          label:   'Sales Report',
          to:      '/sales-report',
          icon:    'receipt',
          premium: false,
          active:  ['Sales Report', 'Sales Invoice'],
        },
        {
          label:   'Profitability',
          to:      '/profitability-report',
          icon:    'coins',
          premium: true,
          active:  ['Profitability Report'],
        },
        {
          label:   'Purchase Report',
          to:      '/purchase-report',
          icon:    'shopping-cart',
          premium: false,
          active:  ['Purchase Report', 'Purchase Report Invoice'],
        },
        {
          label:   'Stock Transfer',
          to:      '/stock-transfer-report',
          icon:    'exchange-alt',
          premium: true,
          active:  ['Stock Transfer Report', 'Stock Transfer Report Details'],
        },
        {
          label:   'Stock Adjustment',
          to:      '/stock-adjustment-report',
          icon:    'sliders-h',
          premium: true,
          active:  ['Stock Adjustment Report', 'Stock Adjustment Report Details'],
        },
        {
          label:   'Top Products',
          to:      '/performance',
          icon:    'trophy',
          premium: true,
          active:  [],
        },
        {
          label:   'Sales by Category',
          to:      '/sales-by-category',
          icon:    'tags',
          premium: true,
          active:  [],
        },
        {
          label:   'Inventory Status',
          to:      '/inventory-status-report',
          icon:    'boxes',
          premium: true,
          active:  [],
        },
        {
          label:   'Audit Trail',
          to:      '/audit-trail',
          icon:    'history',
          premium: true,
          active:  [],
        },
      ],
    };
  },

  computed: {
    currentRouteName() {
      return this.$router.currentRoute.value.name;
    },
  },

  methods: {
    isActive(tab) {
      const current = this.currentRouteName;
      // exact match via router-link active-class already handles the `to` route;
      // this handles the extra aliased route names
      return tab.active.includes(current);
    },

    freemiumPrompt() {
      this.toast.error('Subscribe or upgrade to access this report.');
    },

    async activeSubscription() {
      try {
        const { data } = await axios.get('/subscription/user');
        const hasSub = data.data.some(d => d.status === 1 || d.status === 0);
        this.freemium = !hasSub;
      } catch (error) {
          console.log(error.response?.data?.message);
      }
    },

    updateScrollState() {
      const el = this.$refs.tabsEl;
      if (!el) return;
      this.canScrollLeft  = el.scrollLeft > 4;
      this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    },
  },

  mounted() {
    this.activeSubscription();
    this.$nextTick(() => {
      const el = this.$refs.tabsEl;
      if (el) {
        el.addEventListener('scroll', this.updateScrollState, { passive: true });
        this.updateScrollState();
      }
    });
  },

  beforeUnmount() {
    const el = this.$refs.tabsEl;
    if (el) el.removeEventListener('scroll', this.updateScrollState);
  },
};
</script>

<style scoped>
/* ── Wrapper ─────────────────────────────────────────────────────────── */
.report-tabs-wrap {
  position: relative;
  width: 100%;
  background: #fff;
  border-bottom: 1px solid #e4eaf2;
}

/* ── Scrollable strip ────────────────────────────────────────────────── */
.report-tabs {
  display: flex;
  align-items: center;
  gap: 2px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding: 10px 16px;
  scrollbar-width: none;        /* Firefox */
}
.report-tabs::-webkit-scrollbar { display: none; } /* Chrome/Safari */

/* ── Tab item base ───────────────────────────────────────────────────── */
.tab-item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #7a90b0;
  text-decoration: none;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: all .15s;
  flex-shrink: 0;
  line-height: 1;
}

.tab-item:hover:not(.tab-item--locked) {
  background: #f4f7fb;
  color: #1a2b45;
  text-decoration: none;
}

/* ── Active state ────────────────────────────────────────────────────── */
.tab-item--active,
.router-link-active.tab-item {
  background: rgba(0, 74, 173, .08);
  color: #004AAD;
  font-weight: 700;
  border-color: rgba(0, 74, 173, .18);
}

/* ── Locked (freemium) ───────────────────────────────────────────────── */
.tab-item--locked {
  color: #b0bdd0;
  cursor: not-allowed;
  position: relative;
}
.tab-item--locked:hover {
  background: #fdf4f4;
  color: #c0a080;
  border-color: #f5e0c8;
}

.tab-icon        { font-size: 12px; flex-shrink: 0; }
.tab-icon--lock  { font-size: 11px; color: #c0a080; }

/* ── Scroll fade hints ───────────────────────────────────────────────── */
.scroll-fade {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 48px;
  pointer-events: none;
  opacity: 0;
  transition: opacity .2s;
  z-index: 2;
}
.scroll-fade.visible { opacity: 1; }
.scroll-fade--left  {
  left: 0;
  background: linear-gradient(to right, rgba(255,255,255,.95), transparent);
}
.scroll-fade--right {
  right: 0;
  background: linear-gradient(to left, rgba(255,255,255,.95), transparent);
}

/* ── Mobile ──────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .tab-item { font-size: 12px; padding: 6px 11px; }
}
</style>